/**
 * Quote readiness checklist — scannable prep/send gates for ops UI.
 */

import {
  areQuotePdfsReady,
  isAutomatedPreparationApprovedForSend,
} from "./quotePdfReadiness.js";
import { getQuoteInternalSnapshot } from "../services/quoteOperationalStatus.js";

/**
 * @typedef {"ok"|"warn"|"pending"|"muted"} ChecklistStatus
 */

/**
 * @param {ChecklistStatus} status
 * @returns {string}
 */
export function getChecklistStatusIcon(status) {
  if (status === "ok") return "✓";
  if (status === "warn") return "⚠";
  if (status === "pending") return "○";
  return "·";
}

/**
 * @param {object|null|undefined} quote
 * @param {object|null|undefined} review
 * @returns {object[]}
 */
export function buildQuoteReadinessChecklist(quote, review = null) {
  const items = [];
  const prep = quote?.automatedPreparation;
  const hasAutomatedPrep = Boolean(prep);

  if (hasAutomatedPrep) {
    const inventoryItems = review?.inventory?.items || [];
    const uncertainCount = inventoryItems.filter(
      (row) => row?.catalogueMatch?.matchStatus !== "unique"
    ).length;

    items.push({
      id: "inventory",
      label:
        uncertainCount === 0
          ? "Inventory matched"
          : `${uncertainCount} catalogue item(s) need review`,
      status: uncertainCount === 0 ? "ok" : "warn",
    });

    const reviewReasons = review?.reviewReasons || prep?.reviewReasons || [];
    const pricingReady =
      (review?.reviewState || prep?.reviewState) === "ready" &&
      reviewReasons.length === 0;

    items.push({
      id: "pricing",
      label: pricingReady ? "Pricing complete" : "Pricing needs review",
      status: pricingReady ? "ok" : "warn",
    });
  } else {
    items.push({
      id: "pricing",
      label: "Manual / legacy quote pricing",
      status: "muted",
    });
  }

  items.push({
    id: "pdfs",
    label: areQuotePdfsReady(quote) ? "Quote PDFs ready" : "Quote PDFs not ready",
    status: areQuotePdfsReady(quote) ? "ok" : "warn",
  });

  if (hasAutomatedPrep) {
    items.push({
      id: "approval",
      label: isAutomatedPreparationApprovedForSend(quote)
        ? "Approved for send"
        : "Ops approval required",
      status: isAutomatedPreparationApprovedForSend(quote) ? "ok" : "pending",
    });
  }

  const snap = getQuoteInternalSnapshot(quote);
  if (snap.marginPercent != null) {
    items.push({
      id: "margin",
      label: `Margin ${snap.marginPercent}%`,
      status: snap.marginPercent >= 15 ? "ok" : "warn",
    });
  } else if (snap.revenue != null) {
    items.push({
      id: "margin",
      label: "Margin calculated",
      status: "ok",
    });
  }

  return items;
}

/**
 * @param {object[]} items
 * @returns {{ ready: boolean, warningCount: number, headline: string, tone: "success"|"warning"|"info" }}
 */
export function summarizeReadinessChecklist(items = []) {
  const actionable = items.filter((item) => item.status !== "muted");
  const warningCount = actionable.filter(
    (item) => item.status === "warn" || item.status === "pending"
  ).length;
  const ready = warningCount === 0 && actionable.some((item) => item.status === "ok");

  let headline = "Ready to send";
  let tone = "success";
  if (warningCount > 0) {
    headline =
      warningCount === 1
        ? "Needs review · 1 item"
        : `Needs review · ${warningCount} items`;
    tone = "warning";
  } else if (!actionable.length) {
    headline = "Review quote";
    tone = "info";
  }

  return { ready, warningCount, headline, tone };
}
